import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import debounce from 'lodash/debounce.js';
import { ResizeObserver } from 'vue-resize';
import scrollParent from 'scrollparent';
import { raf } from '../../utils/dom.js';
import PendoResponsive from '../responsive/pendo-responsive.vue.js';
import tableDraggableMixin from '../../mixins/table-draggable.js';
import TableRow from './table-row.vue.js';
import TableCell from './table-cell.js';
import GroupCell from './group-cell.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
let uid = 0;
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var script = {
  components: {
    TableRow,
    TableCell,
    GroupCell,
    ResizeObserver,
    PendoResponsive
  },
  mixins: [tableDraggableMixin],
  inject: ['$table'],
  data() {
    return {
      ready: false,
      scheduleRowUpdate: null
    };
  },
  watch: {
    '$table.tableData': {
      handler() {
        this.updateVisibleRows();
      }
    }
  },
  mounted: _async(function () {
    const _this = this;
    if (_this.$table.scrollStore.pageMode) {
      _this.addPageModeListeners();
    }
    _this.scheduleRowUpdate = raf(_this.updateVisibleRows);
    _this.syncScrollPositions = raf(_this.syncScrollPositions);
    return _await(_this.$nextTick(), function () {
      _this.updateVisibleRows();
      _this.ready = true;
    });
  }),
  beforeDestroy() {
    this.removePageModeListeners();
  },
  methods: {
    getScroll() {
      if (this.$table.scrollStore.pageMode) {
        const {
          top,
          height
        } = this.$el.getBoundingClientRect();
        let start = -top;
        let size = window.innerHeight;
        if (start < 0) {
          size += start;
          start = 0;
        }
        if (start + size > height) {
          size = height - start;
        }
        return {
          start,
          end: start + size
        };
      }
      return {
        start: this.$el.scrollTop,
        end: this.$el.scrollTop + this.$el.clientHeight
      };
    },
    getPageModeListenerTarget() {
      let target = scrollParent(this.$el);
      if (window.document && (target === window.document.documentElement || target === window.document.body)) {
        target = window;
      }
      return target;
    },
    addPageModeListeners() {
      this.pageModeListenerTarget = this.getPageModeListenerTarget();
      this.pageModeListenerTarget.addEventListener('scroll', this.handleScroll, {
        passive: true
      });
      this.pageModeListenerTarget.addEventListener('resize', this.handleResize);
    },
    removePageModeListeners() {
      if (!this.pageModeListenerTarget) {
        return;
      }
      this.pageModeListenerTarget.removeEventListener('scroll', this.handleScroll);
      this.pageModeListenerTarget.removeEventListener('resize', this.handleResize);
      this.pageModeListenerTarget = null;
    },
    handleResize() {
      if (this.ready) {
        this.scheduleRowUpdate.cancel();
        this.scheduleRowUpdate();
      }
    },
    updateScrollStatus: debounce(function () {
      this.$table.scrollStore.scrollActive = false;
      const currentlyHoveredRow = this.$el.querySelector('.pendo-table__row:hover');
      if (!currentlyHoveredRow) {
        return;
      }
      const index = currentlyHoveredRow.getAttribute('data-uid');
      const hoveredRow = this.$table.tableData[index];
      this.$table.setRowHover(hoveredRow, index);
    }, 200, {
      leading: false,
      trailing: true
    }),
    handleScroll(event) {
      const isPageMode = this.$table.scrollStore.pageMode;
      const scrollLeft = isPageMode ? this.$el.scrollLeft : event.target.scrollLeft;
      if (this.$table.overflowX && this.$table.scrollStore.scrollLeft !== scrollLeft) {
        this.syncScrollPositions(isPageMode ? this.$el : event.target);
      }
      if (this.$table.scrollStore.enabled) {
        if (this.$table.scrollStore.scrollTop !== event.target.scrollTop) {
          this.scheduleRowUpdate.cancel();
          this.scheduleRowUpdate();
        }
        this.$table.clearRowHover();
        this.$table.scrollStore.scrollActive = true;
        this.updateScrollStatus();
      }
    },
    syncScrollPositions(_ref) {
      let {
        clientWidth,
        scrollWidth,
        scrollLeft
      } = _ref;
      this.$table.scrollLeftToRight = scrollLeft > 0;
      this.$table.scrollRightToLeft = clientWidth < scrollWidth - scrollLeft;
      this.$table.$refs.tableHeader.$el.scrollLeft = scrollLeft;
      this.$table.scrollStore.scrollLeft = scrollLeft;
    },
    computeStartAndEndIndexes(scroll, buffer, tableDataLength, rowHeight, tableDataHeightMap) {
      scroll.start -= buffer;
      scroll.end += buffer;
      if (rowHeight) {
        const startIndex = Math.max(Math.floor(scroll.start / rowHeight), 0);
        const endIndex = Math.min(Math.ceil(scroll.end / rowHeight), tableDataLength);
        return [startIndex, endIndex];
      }
      const startIndex = Math.max(0, tableDataHeightMap.findIndex(heightAtIndex => heightAtIndex > scroll.start) - 1);
      let stopIndex = tableDataHeightMap.length;
      for (let i = startIndex; i < tableDataHeightMap.length; i++) {
        const heightAtIndex = tableDataHeightMap[i];
        if (heightAtIndex > scroll.end) {
          stopIndex = i;
          break;
        }
      }
      return [startIndex, stopIndex];
    },
    updateVisibleRows() {
      let startIndex = 0;
      let endIndex = 0;
      const {
        tableData,
        tableDataHeightMap,
        scrollStore,
        tableRows
      } = this.$table;
      const {
        rowHeight,
        buffer,
        enabled,
        dynamicRowHeight
      } = scrollStore;
      const count = tableData.length;
      const scroll = this.getScroll();
      if (count && enabled) {
        [startIndex, endIndex] = this.computeStartAndEndIndexes(scroll, buffer, count, dynamicRowHeight ? null : rowHeight, tableDataHeightMap);
      } else {
        startIndex = 0;
        endIndex = count;
      }
      this.$table.scrollStore.startIndex = startIndex;
      this.$table.scrollStore.endIndex = endIndex;
      for (let r = 0, i = startIndex; i < endIndex; r++, i++) {
        let row = tableRows[r];
        if (row) {
          row.nr.index = i;
          row.data = tableData[i];
          if (this.draggedTableRow !== null && this.draggedTableRow.index === i) {
            if (row.nr.id <= this.draggedTableRow.id) {
              r--;
            } else {
              row.nr.index = i - 1;
              row.data = tableData[i - 1];
              i++;
            }
          } else if (this.draggedTableRow !== null && row.nr.id > this.draggedTableRow.id) {
            row.nr.index = i - 1;
            row.data = tableData[i - 1];
          }
          if (dynamicRowHeight) {
            row.nr.key = row.data[this.$table.rowKey];
          }
        } else {
          row = {
            data: tableData[i]
          };
          const nonReactive = {
            id: uid++,
            key: dynamicRowHeight ? row.data[this.$table.rowKey] : uid,
            index: i
          };
          Object.defineProperty(row, 'nr', {
            configurable: false,
            value: nonReactive
          });
          tableRows.push(row);
        }
      }
      if (tableRows.length + startIndex > count) {
        tableRows.length = Math.max(count - startIndex, 0);
      }
      if (tableRows.length > endIndex - startIndex) {
        tableRows.length = Math.max(endIndex - startIndex, 0);
      }
      if (rowHeight && !dynamicRowHeight) {
        this.$table.scrollStore.scrollTop = Math.floor(startIndex * rowHeight);
      } else {
        this.$table.scrollStore.scrollTop = tableDataHeightMap[startIndex];
      }
      if (dynamicRowHeight) {
        this.$nextTick(() => {
          const updatedRows = tableRows.reduce((acc, row) => {
            const {
              height
            } = document.querySelector(`[data-uid="${row.nr.index}"]`).getBoundingClientRect();
            if (row.data.height !== height && height) {
              acc.push(_objectSpread2(_objectSpread2({}, row.data), {}, {
                height
              }));
            }
            return acc;
          }, []);
          if (updatedRows.length) {
            this.$table.updateRows(updatedRows);
          }
        });
      }
      this.$table.calculateStickyElementPositions();
    }
  }
};

export { script as default };
//# sourceMappingURL=table-body.vue2.js.map
